import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { apiFetch } from '@/lib/api';
import { useAnonymousRead } from '@/lib/usePages';

// Mirror of `MeResponse` from tres-api/src/handlers/me.rs.
export interface Me {
  user_id: string;
  display_name: string;
  role: 'Owner' | 'Admin' | 'PowerUser' | 'User' | 'Auditor' | 'Anonymous';
  allowed_actions: string[];
  /** Active storage backend — "rdf" or "qros". */
  backend: string;
  /** Provider and model currently answering questions; null when AI is off. */
  ai_provider: string | null;
  ai_model: string | null;
  /** Terms of Use version this person accepted (the Terms' revision date); null until they do. */
  terms_accepted_version: string | null;
  /** This site's billing standing from the fourteen-day notice onward; sent to
   *  an Owner or Admin only, null otherwise and for a site in good standing. */
  site_standing: SiteStanding | null;
}

/** Mirror of `StandingNotice` in tres-api/src/entitlement.rs. Times are Unix
 *  seconds. */
export interface SiteStanding {
  kind: 'ending' | 'grace' | 'lapsed';
  cause: 'term_end' | 'payment_overdue';
  ends_at: number;
  offline_at: number;
  days_remaining: number | null;
  notice: 'fourteen_days' | 'seven_days' | 'day_before' | null;
}

/** A visitor who has not signed in, on an instance that serves them. */
export function isGuest(me: Me | undefined): boolean {
  return me?.role === 'Anonymous';
}

/** QROS-native affordances (statement-tag authoring/display) are gated on
 *  this — the RDF version surfaces no tags. */
export function isQros(me: Me | undefined): boolean {
  return me?.backend === 'qros';
}

/** A failed `/api/me`, with the status it failed with. */
export class MeRequestError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** Rate limiting (429), a server that is restarting or overloaded (5xx), and a
 *  request that never reached the server (status 0) pass on their own. A 401
 *  or 403 is an answer about the credential and is not asked again. */
function meFailureIsTransient(error: Error): boolean {
  if (!(error instanceof MeRequestError)) return true;
  return error.status === 0 || error.status === 429 || error.status >= 500;
}

/** Six retries over about 45 seconds: long enough to outlast a burst window
 *  (10 s) or a deploy's unhealthy gap, short enough that a lasting failure is
 *  shown as one rather than as a page that never finishes loading. */
const ME_TRANSIENT_RETRIES = 6;

export function meRetry(failureCount: number, error: Error): boolean {
  return meFailureIsTransient(error) && failureCount < ME_TRANSIENT_RETRIES;
}

export function meRetryDelay(failureCount: number): number {
  return Math.min(1000 * 2 ** failureCount, 15_000);
}

export function useMe() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const anonymousRead = useAnonymousRead();

  return useQuery<Me, Error>({
    queryKey: ['me', token ?? 'anon'],
    // Runs for signed-out visitors too when the instance serves them. This is
    // what makes the rest of the SPA correct for guests without touching it:
    // every write affordance is already gated on a `can*` helper reading this
    // response, so a guest whose `allowed_actions` omit ImportData or
    // EditTaxonomy loses those controls the same way an under-privileged
    // signed-in user does. Hunting down write buttons one by one would have
    // been a list that drifts; this is the same list the server enforces.
    enabled: Boolean(token) || anonymousRead.data === true,
    queryFn: async () => {
      let r;
      try {
        r = await apiFetch<Me>('/api/me', token);
      } catch (e) {
        throw new MeRequestError(e instanceof Error ? e.message : String(e), 0);
      }
      if (!r.ok) {
        throw new MeRequestError(r.message, r.status);
      }
      return r.data;
    },
    // Every page decision waits on the role, so a refused or dropped request
    // must be asked again rather than leave the visitor with none.
    retry: meRetry,
    retryDelay: meRetryDelay,
    // Identity changes only on login/logout — long stale time is fine.
    staleTime: 5 * 60 * 1000,
  });
}

export function canImportData(me: Me | undefined): boolean {
  if (!me) return false;
  return me.allowed_actions.includes('ImportData');
}

/** File a bug report through the in-app reporter. Every signed-in role holds
 *  it; a guest does not (the reporter writes under the company's credential). */
export function canFileIssue(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('FileIssue') ?? false;
}

export function canManageDashboards(me: Me | undefined): boolean {
  if (!me) return false;
  return me.allowed_actions.includes('ManageDashboards');
}

/** Admin user/role management (Admin). */
export function canManageUsers(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ManageUsers') ?? false;
}

/** Invite, promote, demote or disable an Admin or an Owner (Owner only). */
export function canManageAdmins(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ManageAdmins') ?? false;
}

/** Whether this user may download a whole graph as a file: Admin and Owner. */
export function canExportStore(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ExportStore') ?? false;
}

/** Whether this user may read the visualization routes. Unknown until `me` loads. */
export function canVisualize(me: Me | undefined): boolean {
  return me === undefined || me.allowed_actions.includes('Visualize');
}

/** Whether this user may browse vocabularies. Unknown until `me` loads. */
export function canBrowseTaxonomy(me: Me | undefined): boolean {
  return me === undefined || me.allowed_actions.includes('BrowseTaxonomy');
}

/** Read the audit log + usage (Admin + Auditor). */
export function canViewAudit(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ViewAuditLog') ?? false;
}

/** See, grade and export every user's questions to the assistant
 *  (Owner, Admin, Auditor). Everyone else sees only their own. */
export function canReviewAllQuestions(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ReviewAllQuestions') ?? false;
}

/** Change instance settings incl. page visibility (Admin). */
export function canManageSettings(me: Me | undefined): boolean {
  return me?.allowed_actions.includes('ServerSettings') ?? false;
}

/** Whether the Admin nav entry / page should be reachable at all —
 *  any admin-console capability (Admin gets all; Auditor gets audit). */
export function canAccessAdmin(me: Me | undefined): boolean {
  return canManageUsers(me) || canManageSettings(me) || canViewAudit(me);
}
